import { useEffect, useReducer, useState } from "react";
import type { AbstractAgent } from "@ag-ui/client";

export interface AgentRunState {
  // The agent instance, re-rendered on every lifecycle event.
  agent: AbstractAgent;
  // True unless the last run ended via RUN_ERROR. Lets callers tell a deferred
  // pause (clean RUN_FINISHED, un-resulted call) from a crash. Initialised true
  // so a history-reloaded pause (no terminal event this session) reads as clean.
  runEndedCleanly: boolean;
}

// Subscribes to the agent's lifecycle events and forces a re-render so
// consumers can read `agent.messages` / `agent.isRunning` directly. Replaces
// CopilotKit's `useAgent` to avoid its chat-UI dep chain (streamdown → katex).
//
// ag-ui's HttpAgent only completes (clearing isRunning) on socket close, which
// keep-alive lags by seconds — so on the terminal RUN_FINISHED / RUN_ERROR
// event we detach to end the stream now. detach (not abortRun) preserves
// success-path completion; onRunFinalized covers other terminations.
export function useAgent(agent: AbstractAgent): AgentRunState {
  const [, forceRerender] = useReducer((x: number) => x + 1, 0);
  const [runEndedCleanly, setRunEndedCleanly] = useState(true);
  useEffect(() => {
    let active = true;

    // Coalesce multiple synchronous notifications into a single React re-render.
    let batchScheduled = false;
    const batchedRerender = () => {
      if (!active || batchScheduled) return;
      batchScheduled = true;
      queueMicrotask(() => {
        batchScheduled = false;
        if (active) forceRerender();
      });
    };

    // Detach to end the stream now; the run won't complete until socket close.
    const finishRun = () => {
      void agent.detachActiveRun();
      batchedRerender();
    };

    const sub = agent.subscribe({
      onMessagesChanged: batchedRerender,
      onStateChanged: batchedRerender,
      onRunInitialized: batchedRerender,
      // Disjoint terminal events (one or the other, never both). Record which
      // fired so a deferred pause is distinguishable from a crash.
      onRunFinishedEvent: () => {
        setRunEndedCleanly(true);
        finishRun();
      },
      onRunErrorEvent: () => {
        setRunEndedCleanly(false);
        finishRun();
      },
      onRunFinalized: batchedRerender,
    });
    return () => {
      active = false;
      sub.unsubscribe();
    };
  }, [agent]);
  return { agent, runEndedCleanly };
}
